import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function ExteriorDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Exterior Car Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Ventura County', 'Camarillo', 'Oxnard', 'Ventura', 'Thousand Oaks'],
    description: 'Professional mobile exterior car detailing services including hand wash, clay bar treatment, wheel cleaning, and wax protection.'
  };
  const faqs = [{
    question: 'What is included in exterior detailing?',
    answer: 'Our exterior detail includes a safe hand wash, deep wheel and tire cleaning, clay bar decontamination, bug and tar removal, premium wax or sealant application, and tire and trim dressing.'
  }, {
    question: 'Will exterior detailing remove swirl marks?',
    answer: 'A standard exterior detail cleans and protects but does not remove swirl marks. For added gloss and water beading, pair it with our hybrid ceramic spray protection.'
  }, {
    question: 'How long does exterior protection last?',
    answer: 'Our hybrid ceramic wax and polymer sealants typically provide several months of protection and water beading, depending on conditions and how often the vehicle is washed.'
  }, {
    question: 'How much does exterior detailing cost?',
    answer: 'Exterior detailing starts at $160, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you bring your own water?',
    answer: 'Yes. We bring our own spot-free water and detailing equipment. We require access to a standard electrical outlet at the service location.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/#services'
  }, {
    name: 'Exterior Detailing',
    item: 'https://www.fulldetailpro.com/services/exterior-detailing'
  }];
  return <ServicePageLayout title="Exterior Detailing Ventura County" subtitle="Protect your paint and restore a brilliant shine with our premium mobile exterior detailing services. We bring the ultimate wash and wax directly to you." metaDescription="Top-rated mobile exterior detailing in Ventura County \u2014 Camarillo, Oxnard & Ventura. Hand wash, clay bar & ceramic wax protection. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-762" data-mp="28chnhkqf4ltq">
      <p data-id="element-763" data-mp="1kldeijit3z5h">
        Your vehicle's exterior faces a constant barrage of environmental
        hazards: UV rays, bird droppings, tree sap, road tar, and industrial
        fallout. Over time, these contaminants degrade your clear coat, leaving
        your paint looking dull and feeling rough. At Mike's Pro Detail, our{' '}
        <strong data-id="element-764" data-mp="3az2rjoa9cfsi">mobile exterior car detailing</strong> service is designed to
        safely remove these contaminants, restore a deep gloss, and apply
        durable protection. Whether you need car detailing in Camarillo,
        Thousand Oaks, or anywhere in Ventura County, our mobile unit provides a
        superior alternative to automated car washes that scratch and swirl your
        paint.
      </p>

      <h2 data-id="element-765" data-mp="1ahf0r3eo7qhi">Our Exterior Detailing Process</h2>
      <p data-id="element-766" data-mp="dsh6rneqvwle">
        We utilize safe, scratch-free wash methods and premium products to
        ensure your vehicle looks its absolute best. Our exterior detailing
        package includes:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-767" data-mp="msvwr02nlzv8">
        <li className="flex items-start" data-id="element-768" data-mp="mrb0n60oy8v9">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-769" data-mp="rrgsam6ynvdc" />
          <span data-id="element-770" data-mp="rpchdkan9w3k">
            A meticulous hand wash using premium microfiber towels and
            pH-neutral soap to safely lift dirt without inducing swirl marks.
          </span>
        </li>
        <li className="flex items-start" data-id="element-771" data-mp="238yql65rw2sh">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-772" data-mp="21ox338wjnz74" />
          <span data-id="element-773" data-mp="1wqlu10e2pspy">
            <strong data-id="element-774" data-mp="1wr5cqjbjsl51">Deep Wheel & Tire Cleaning:</strong> We use specialized
            brushes and non-acidic cleaners to remove baked-on brake dust from
            wheel barrels, faces, and tires.
          </span>
        </li>
        <li className="flex items-start" data-id="element-775" data-mp="26nqlnfqouge5">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-776" data-mp="26r5zygcjflei" />
          <span data-id="element-777" data-mp="2kmzzi8rf8yey">
            <strong data-id="element-778" data-mp="2kmhrkevecvr7">Clay Bar Treatment:</strong> A crucial step that removes
            embedded contaminants (like overspray and rail dust) that washing
            alone cannot remove, leaving the paint smooth as glass.
          </span>
        </li>
        <li className="flex items-start" data-id="element-779" data-mp="2pf17hihk42z6">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-780" data-mp="2pg2r4bcn1xsi" />
          <span data-id="element-781" data-mp="33fu3y8rf584x">
            <strong data-id="element-782" data-mp="33fbycliv9hkc">Bug & Tar Removal:</strong> Safe breakdown and removal of
            stubborn bug splatter and road tar from the front bumper and lower
            rocker panels.
          </span>
        </li>
        <li className="flex items-start" data-id="element-783" data-mp="3hzofwa7d1kdo">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-784" data-mp="3n1dt9wi22a4x" />
          <span data-id="element-785" data-mp="3myhsm0etp7cs">
            <strong data-id="element-786" data-mp="391cqucm56unc">Premium Wax / Sealant Application:</strong> Application of a
            high-quality hybrid ceramic wax or polymer sealant to provide months
            of protection and intense water beading.
          </span>
        </li>
        <li className="flex items-start" data-id="element-787" data-mp="2z9ovw1qdmvh4">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-788" data-mp="341zwz7wusnb9" />
          <span data-id="element-789" data-mp="343azl4ekbp0s">
            <strong data-id="element-790" data-mp="343t5ytuxvjmf">Tire & Trim Dressing:</strong> Application of a no-sling,
            UV-blocking dressing to tires and faded exterior plastics to restore
            a rich, dark appearance.
          </span>
        </li>
      </ul>

      <h2 data-id="element-791" data-mp="2gc5jqp8mgwqh">Why Exterior Detailing is Essential</h2>
      <p data-id="element-792" data-mp="2geibtv83gnh9">
        Regular exterior detailing is preventative maintenance for your
        vehicle's paint. The Southern California sun can quickly cause clear
        coat failure and oxidation if the paint is left unprotected. By
        regularly removing bonded contaminants through a clay bar treatment and
        applying a protective wax or sealant, you create a sacrificial barrier
        against the elements. This not only keeps your car looking stunning but
        also preserves its resale value and prevents costly paint repairs down
        the line.
      </p>

      <h2 data-id="element-793" data-mp="19tmaal6m20wz">The Premier Choice for Mobile Auto Detailing Camarillo CA</h2>
      <p data-id="element-794" data-mp="14ut6jkh4loa0">
        When you search for "Mobile Detailing Ventura County" or need reliable
        service in Thousand Oaks, Mike's Pro Detail is the trusted choice. We
        bring our own spot-free water and detailing equipment, and we require
        access to a standard electrical outlet at the service location, meaning
        we can perform a flawless exterior detail in your driveway, at your
        office parking lot, or even at your apartment complex. We treat every vehicle as if it were
        our own, ensuring that every inch of your exterior receives the
        attention it deserves.
      </p>

      <h2 data-id="element-795" data-mp="212pez2h1iwv0">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-796" data-mp="20za1g430lvvp">
        <Link to="/services/hybrid-ceramic-spray" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-797" data-mp="251ybgf3gt8u9">
          Hybrid Ceramic Spray &rarr;
        </Link>
        <Link to="/services/full-car-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-798" data-mp="2jrcn5tuik2ct">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-799" data-mp="2sqpv2i2zoivf">
          Mobile Detailing &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}